import i18next from 'i18next';

import {collectErrorMessages} from '../../common/js/error-messages';
import {displayAdOverlay, removeAdOverlay} from './dom-manipulation';
import {focusOperationReport, renderOperationReport} from './operation-report';

const REPORT_SELECTOR = '#inventory-operation-report';
const SUCCESS_STATUSES = new Set(['success', 'copied', 'completed']);
const FAILED_STATUSES = new Set(['error', 'failed', 'failure', 'api_error', 'validation_error', 'completed_with_errors']);
const SCHEDULED_STATUSES = new Set(['scheduled', 'queued', 'planned', 'retry_scheduled']);
const UNFINISHED_STATUSES = new Set(['skipped', 'superseded', 'running', 'started', 'cancelled']);
const STATE_PRIORITY = {
    success: 0,
    scheduled: 1,
    warning: 2,
    failed: 3,
};
const inventoryOverlayTokens = new Map();

const t = (key, defaultValue, options = {}) => i18next.t(
    `ads:inventoryFeedback.${key}`,
    {defaultValue, ...options},
);

const getGenericFailureMessage = () => {
    const fallback = 'The operation failed for this item.';
    const translated = t('genericFailure', fallback);
    const language = String(i18next.resolvedLanguage || i18next.language || '').toLowerCase();
    return language === 'en' || language.startsWith('en-') ? fallback : translated;
};

const readAggregateCount = (response, ...keys) => {
    const source = response && typeof response === 'object' ? response : {};
    const key = keys.find((candidate) => Object.prototype.hasOwnProperty.call(source, candidate));
    if (!key) {
        return {
            provided: false,
            value: null,
            numericValue: 0,
            isValid: true,
        };
    }
    const value = source[key];
    const isNumericValue = typeof value === 'number';
    const isCanonicalNumericString = (
        typeof value === 'string'
        && /^(0|[1-9]\d*)$/.test(value)
    );
    const numericValue = isNumericValue || isCanonicalNumericString ? Number(value) : NaN;
    const isValid = Number.isFinite(numericValue) && Number.isInteger(numericValue) && numericValue >= 0;
    return {
        provided: true,
        value: isValid ? numericValue : value,
        numericValue: isValid ? numericValue : 0,
        isValid,
    };
};

const normalizeId = (value) => String(value ?? '').trim();

const getAccountId = (item) => normalizeId(
    item?.account_id
    ?? item?.accountId
    ?? item?.target_account_id,
);

const getAdvertId = (item) => normalizeId(
    item?.ad_id
    ?? item?.advert_id
    ?? item?.adId
    ?? item?.source_olx_ad_id,
);

const getItemIdentityKey = (item) => {
    const accountId = getAccountId(item);
    const adId = getAdvertId(item);
    return accountId || adId ? `${accountId}:${adId}` : null;
};

const getItemKey = (item, fallbackIndex = 0) => {
    return getItemIdentityKey(item) || `item:${fallbackIndex}`;
};

const findSubmittedItem = (entry, submittedItems, index) => {
    const key = getItemKey(entry, index);
    return submittedItems.find((item, submittedIndex) => getItemKey(item, submittedIndex) === key)
        || submittedItems[index]
        || {};
};

const getEntryErrorMessages = (entry) => {
    const status = String(entry?.status || '').toLowerCase();
    const values = [
        entry?.errors,
        entry?.error_message,
        entry?.error,
        entry?.latest_attempt?.error_message,
        entry?.latest_attempt?.response_payload?.errors,
        entry?.payload_snapshot?.last_execution_errors,
    ];
    if (!status || FAILED_STATUSES.has(status)) {
        values.push(entry?.reason, entry?.message);
    }
    return collectErrorMessages(values);
};

const getEntryUnfinishedMessages = (entry) => {
    const messages = collectErrorMessages([
        entry?.reason,
        entry?.message,
        entry?.errors,
        entry?.error_message,
        entry?.error,
        entry?.latest_attempt?.error_message,
        entry?.latest_attempt?.response_payload?.errors,
        entry?.payload_snapshot?.last_execution_errors,
    ]);
    return messages.length
        ? messages
        : [t('notCompleted', 'Not completed')];
};

const getEntryWarningMessages = (entry) => {
    const imageWarnings = entry?.warnings?.image_downloads;
    const otherWarnings = entry?.warnings && typeof entry.warnings === 'object' && !Array.isArray(entry.warnings)
        ? Object.fromEntries(Object.entries(entry.warnings).filter(([key]) => key !== 'image_downloads'))
        : entry?.warnings;
    const messages = collectErrorMessages([otherWarnings, imageWarnings?.items, imageWarnings?.errors]);
    if (!messages.length && Number(imageWarnings?.count || 0) > 0) {
        messages.push(t('imageWarning', 'Some images could not be downloaded.'));
    }
    return messages;
};

const getEntryClassification = (entry) => {
    const status = String(entry?.status || '').toLowerCase();
    if (UNFINISHED_STATUSES.has(status)) {
        return {
            state: 'warning',
            completed: false,
            messages: getEntryUnfinishedMessages(entry),
        };
    }
    const errorMessages = getEntryErrorMessages(entry);
    if (FAILED_STATUSES.has(status) || errorMessages.length) {
        return {
            state: 'failed',
            completed: false,
            messages: errorMessages.length
                ? errorMessages
                : [t('genericFailure', 'The operation failed for this item.')],
        };
    }
    const warningMessages = getEntryWarningMessages(entry);
    if (warningMessages.length) {
        return {
            state: 'warning',
            completed: true,
            messages: warningMessages,
        };
    }
    if (SUCCESS_STATUSES.has(status)) {
        return {state: 'success', completed: true, messages: []};
    }
    if (SCHEDULED_STATUSES.has(status)) {
        return {state: 'scheduled', completed: false, messages: []};
    }
    if (!status) {
        return null;
    }
    return {
        state: 'warning',
        completed: false,
        messages: getEntryUnfinishedMessages(entry),
    };
};

const getItemLabel = (entry, submittedItem, adId) => (
    String(
        entry?.title
        || entry?.name
        || entry?.payload_snapshot?.title
        || submittedItem?.title
        || submittedItem?.name
        || '',
    ).trim()
    || (adId
        ? t('advertFallback', 'Advert #{{id}}', {id: adId})
        : t('genericFailure', 'The operation failed for this item.'))
);

const mergeMessages = (current, incoming) => {
    incoming.forEach((message) => {
        if (!current.includes(message)) {
            current.push(message);
        }
    });
};

const collectResponseEntries = (response) => [
    ...(Array.isArray(response?.executions) ? response.executions : []),
    ...(Array.isArray(response?.items) ? response.items : []),
    ...(Array.isArray(response?.results?.items) ? response.results.items : []),
];

const getAggregateConsistency = ({
    success,
    failed,
    scheduled,
    submitted,
    submittedCount,
    localSubmittedCount,
    detailedCounts = null,
}) => {
    const counts = [success, failed, scheduled, submittedCount];
    const hasAggregateCounts = counts.some((count) => count.provided);
    if (!hasAggregateCounts) {
        return {hasAggregateCounts, isConsistent: true};
    }
    const countsAreValid = counts.every((count) => !count.provided || count.isValid);
    const outcomesMatchSubmitted = (
        success.numericValue + failed.numericValue + scheduled.numericValue === submitted
    );
    const localItemsMatchSubmitted = localSubmittedCount === 0 || localSubmittedCount === submitted;
    const detailsMatchCounts = !detailedCounts || (
        detailedCounts.success <= success.numericValue
        && detailedCounts.failed <= failed.numericValue
        && detailedCounts.scheduled <= scheduled.numericValue
    );
    return {
        hasAggregateCounts,
        isConsistent: (
            countsAreValid
            && outcomesMatchSubmitted
            && localItemsMatchSubmitted
            && detailsMatchCounts
        ),
    };
};

const getAggregateFallbackState = ({
    success,
    failed,
    scheduled,
    submitted,
    hasAggregateCounts,
    isConsistent,
}) => {
    if (!hasAggregateCounts) {
        return 'success';
    }
    if (!isConsistent) {
        return null;
    }
    if (submitted > 0 && failed === submitted && success === 0 && scheduled === 0) {
        return 'failed';
    }
    if (submitted > 0 && success === submitted && failed === 0 && scheduled === 0) {
        return 'success';
    }
    if (submitted > 0 && scheduled === submitted && success === 0 && failed === 0) {
        return 'scheduled';
    }
    return null;
};

const getStatuslessDetailInference = ({
    entries,
    submittedItems,
    success,
    failed,
    scheduled,
    submitted,
    aggregateConsistency,
}) => {
    const submittedIdentityKeys = new Set(submittedItems.map(getItemIdentityKey).filter(Boolean));
    const responseIdentityKeys = new Set(entries.map(getItemIdentityKey).filter(Boolean));
    const hasExactIdentityCoverage = (
        submittedItems.length > 0
        && submittedIdentityKeys.size === submittedItems.length
        && responseIdentityKeys.size === submittedIdentityKeys.size
        && [...responseIdentityKeys].every((key) => submittedIdentityKeys.has(key))
    );
    const groupedDetails = new Map();
    entries.forEach((entry, index) => {
        const submittedItem = findSubmittedItem(entry, submittedItems, index);
        const accountId = getAccountId(entry) || getAccountId(submittedItem);
        const adId = getAdvertId(entry) || getAdvertId(submittedItem);
        const key = accountId || adId ? `${accountId}:${adId}` : `item:${index}`;
        const classification = getEntryClassification(entry);
        if (!groupedDetails.has(key)) {
            groupedDetails.set(key, {classification: null});
        }
        if (!classification) {
            return;
        }
        const groupedDetail = groupedDetails.get(key);
        if (
            !groupedDetail.classification
            || STATE_PRIORITY[classification.state] > STATE_PRIORITY[groupedDetail.classification.state]
        ) {
            groupedDetail.classification = {...classification};
        } else if (classification.state === groupedDetail.classification.state) {
            groupedDetail.classification.completed = (
                groupedDetail.classification.completed && classification.completed
            );
        }
    });

    const unresolvedKeys = new Set(
        [...groupedDetails.entries()]
            .filter(([, detail]) => !detail.classification)
            .map(([key]) => key),
    );
    if (!unresolvedKeys.size) {
        return {state: null, hasAmbiguousEntries: false, unresolvedKeys};
    }
    if (
        !aggregateConsistency.hasAggregateCounts
        || !aggregateConsistency.isConsistent
        || groupedDetails.size !== submitted
        || !hasExactIdentityCoverage
    ) {
        return {state: null, hasAmbiguousEntries: true, unresolvedKeys};
    }

    const explicitCounts = {success: 0, failed: 0, scheduled: 0};
    for (const detail of groupedDetails.values()) {
        if (!detail.classification) {
            continue;
        }
        let outcome = detail.classification.state;
        if (outcome === 'warning' && detail.classification.completed) {
            outcome = 'success';
        }
        if (!Object.prototype.hasOwnProperty.call(explicitCounts, outcome)) {
            return {state: null, hasAmbiguousEntries: true, unresolvedKeys};
        }
        explicitCounts[outcome] += 1;
    }

    const remainingCounts = {
        success: success - explicitCounts.success,
        failed: failed - explicitCounts.failed,
        scheduled: scheduled - explicitCounts.scheduled,
    };
    const unknownCount = unresolvedKeys.size;
    const candidates = Object.entries(remainingCounts).filter(([, count]) => count === unknownCount);
    const isUniqueRemainder = (
        Object.values(remainingCounts).every((count) => count >= 0)
        && Object.values(remainingCounts).reduce((total, count) => total + count, 0) === unknownCount
        && candidates.length === 1
    );
    return {
        state: isUniqueRemainder ? candidates[0][0] : null,
        hasAmbiguousEntries: !isUniqueRemainder,
        unresolvedKeys,
    };
};

export const normalizeInventoryOperationResult = ({operationType, response = {}, submittedItems = []}) => {
    const normalizedSubmittedItems = Array.isArray(submittedItems) ? submittedItems : [];
    const entries = collectResponseEntries(response);
    const backendSuccessCount = readAggregateCount(
        response,
        'success_count',
        'copied_count',
    );
    const backendFailedCount = readAggregateCount(response, 'error_count', 'failed_count');
    const backendScheduledCount = readAggregateCount(response, 'scheduled_count');
    const backendSubmittedCount = readAggregateCount(response, 'submitted_count', 'total_count');
    const aggregateSuccess = backendSuccessCount.numericValue;
    const aggregateFailed = backendFailedCount.numericValue;
    const aggregateScheduled = backendScheduledCount.numericValue;
    const outcomeCount = aggregateSuccess + aggregateFailed + aggregateScheduled;
    const inferredSubmitted = Math.max(
        normalizedSubmittedItems.length,
        outcomeCount,
    );
    const aggregateSubmitted = backendSubmittedCount.provided && backendSubmittedCount.isValid
        ? backendSubmittedCount.numericValue
        : inferredSubmitted;
    const initialAggregateConsistency = getAggregateConsistency({
        success: backendSuccessCount,
        failed: backendFailedCount,
        scheduled: backendScheduledCount,
        submitted: aggregateSubmitted,
        submittedCount: backendSubmittedCount,
        localSubmittedCount: normalizedSubmittedItems.length,
    });
    const statuslessDetailInference = getStatuslessDetailInference({
        entries,
        submittedItems: normalizedSubmittedItems,
        success: aggregateSuccess,
        failed: aggregateFailed,
        scheduled: aggregateScheduled,
        submitted: aggregateSubmitted,
        aggregateConsistency: initialAggregateConsistency,
    });
    const groupedItems = new Map();

    const mergeEntry = (entry, index) => {
        const submittedItem = findSubmittedItem(entry, normalizedSubmittedItems, index);
        const accountId = getAccountId(entry) || getAccountId(submittedItem);
        const adId = getAdvertId(entry) || getAdvertId(submittedItem);
        const key = accountId || adId ? `${accountId}:${adId}` : `item:${index}`;
        const classification = getEntryClassification(entry) || (
            statuslessDetailInference.state && statuslessDetailInference.unresolvedKeys.has(key)
                ? getEntryClassification({...entry, status: statuslessDetailInference.state})
                : null
        );
        if (!classification) {
            return;
        }
        const {state, completed, messages} = classification;

        if (!groupedItems.has(key)) {
            groupedItems.set(key, {
                key,
                accountId,
                adId,
                label: getItemLabel(entry, submittedItem, adId),
                state,
                completed,
                messages: [],
            });
        }
        const groupedItem = groupedItems.get(key);
        if (STATE_PRIORITY[state] > STATE_PRIORITY[groupedItem.state]) {
            groupedItem.state = state;
            groupedItem.completed = completed;
        } else if (state === groupedItem.state) {
            groupedItem.completed = groupedItem.completed && completed;
        }
        mergeMessages(groupedItem.messages, messages);
    };

    if (entries.length) {
        entries.forEach(mergeEntry);
    } else {
        const fallbackState = getAggregateFallbackState({
            success: aggregateSuccess,
            failed: aggregateFailed,
            scheduled: aggregateScheduled,
            submitted: aggregateSubmitted,
            hasAggregateCounts: initialAggregateConsistency.hasAggregateCounts,
            isConsistent: initialAggregateConsistency.isConsistent,
        });
        if (fallbackState) {
            normalizedSubmittedItems.forEach((item, index) => mergeEntry({
                ...item,
                status: fallbackState,
            }, index));
        }
    }

    const itemStates = [...groupedItems.values()];
    const failedFallback = itemStates.filter((item) => item.state === 'failed').length;
    const successFallback = itemStates.filter((item) => item.completed).length;
    const scheduledFallback = itemStates.filter((item) => item.state === 'scheduled').length;
    const success = backendSuccessCount.provided ? backendSuccessCount.value : successFallback;
    const failed = backendFailedCount.provided ? backendFailedCount.value : failedFallback;
    const scheduled = backendScheduledCount.provided ? backendScheduledCount.value : scheduledFallback;
    const warnings = itemStates.filter((item) => item.state === 'warning').length;
    const submitted = backendSubmittedCount.provided
        ? backendSubmittedCount.value
        : Math.max(aggregateSubmitted, itemStates.length);
    const aggregateConsistency = getAggregateConsistency({
        success: backendSuccessCount,
        failed: backendFailedCount,
        scheduled: backendScheduledCount,
        submitted: aggregateSubmitted,
        submittedCount: backendSubmittedCount,
        localSubmittedCount: normalizedSubmittedItems.length,
        detailedCounts: entries.length ? {
            success: successFallback,
            failed: failedFallback,
            scheduled: scheduledFallback,
        } : null,
    });
    const successEvidence = backendSuccessCount.provided ? aggregateSuccess : successFallback;
    const failedEvidence = backendFailedCount.provided ? aggregateFailed : failedFallback;
    const scheduledEvidence = backendScheduledCount.provided ? aggregateScheduled : scheduledFallback;
    const hasFailureEvidence = failedEvidence > 0 || failedFallback > 0;
    const hasSuccessOrPlanningEvidence = (
        successEvidence > 0
        || scheduledEvidence > 0
        || successFallback > 0
        || scheduledFallback > 0
    );
    let severity = hasFailureEvidence && !hasSuccessOrPlanningEvidence
        ? 'danger'
        : (hasFailureEvidence || warnings > 0 ? 'warning' : 'success');
    if (aggregateConsistency.hasAggregateCounts && !aggregateConsistency.isConsistent) {
        const hasOnlyDetailedFailureEvidence = (
            entries.length > 0
            && failedFallback > 0
            && successFallback === 0
            && scheduledFallback === 0
            && aggregateSuccess === 0
            && aggregateScheduled === 0
        );
        severity = hasOnlyDetailedFailureEvidence ? 'danger' : 'warning';
    }
    if (statuslessDetailInference.hasAmbiguousEntries && severity === 'success') {
        severity = 'warning';
    }
    let problems = entries.length
        ? itemStates.filter((item) => ['failed', 'warning'].includes(item.state))
        : [];

    if (statuslessDetailInference.hasAmbiguousEntries) {
        problems.push({
            key: 'operation',
            accountId: '',
            adId: '',
            label: getGenericFailureMessage(),
            state: 'warning',
            messages: [],
        });
    }

    if (!problems.length && severity !== 'success') {
        const genericFailure = getGenericFailureMessage();
        problems = [{
            key: 'operation',
            accountId: '',
            adId: '',
            label: genericFailure,
            state: severity === 'danger' ? 'failed' : 'warning',
            messages: [],
        }];
    }

    return {
        operationType,
        submitted,
        success,
        failed,
        warnings,
        scheduled,
        severity,
        items: itemStates,
        problems,
        hiddenProblemCount: 0,
    };
};

export const clearInventoryOperationReport = () => {
    $(REPORT_SELECTOR).empty();
};

export const showInventoryOperationReport = (result) => {
    const container = $(REPORT_SELECTOR);
    if (!container.length) {
        return null;
    }
    if (!result || result.severity === 'success') {
        clearInventoryOperationReport();
        return null;
    }

    return renderOperationReport({
        container: container[0],
        model: {
            variant: result.severity,
            title: result.severity === 'danger'
                ? t('dangerTitle', 'Operation failed')
                : t('warningTitle', 'Some items need attention'),
            counts: [
                {key: 'success', label: t('completedCount', 'Completed'), value: result.success, tone: 'success', valueAttributes: {'data-inventory-feedback-count': 'success'}},
                {key: 'failed', label: t('errorsCount', 'Errors'), value: result.failed, tone: 'danger', valueAttributes: {'data-inventory-feedback-count': 'failed'}},
                {key: 'warnings', label: t('warningsCount', 'Warnings'), value: result.warnings, tone: 'warning', valueAttributes: {'data-inventory-feedback-count': 'warnings'}},
                {key: 'scheduled', label: t('scheduled', 'Scheduled'), value: result.scheduled, tone: 'info', valueAttributes: {'data-inventory-feedback-count': 'scheduled'}},
                {key: 'submitted', label: t('selected', 'Selected'), value: result.submitted, tone: 'neutral', alwaysVisible: true, valueAttributes: {'data-inventory-feedback-count': 'submitted'}},
            ],
            details: {
                label: t('problems', 'Problems'),
                total: result.problems.length + result.hiddenProblemCount,
                items: result.problems.map((problem) => ({
                    label: problem.label,
                    message: problem.messages.join(', '),
                })),
            },
        },
        aliases: {
            root: 'inventory-operation-report',
            variant: (variant) => `inventory-operation-report--${variant}`,
            header: 'inventory-operation-report__header',
            heading: 'inventory-operation-report__heading',
            close: 'inventory-operation-report__close',
            counts: 'inventory-operation-report__counts',
            count: 'inventory-operation-report__count',
            details: 'inventory-operation-report__details',
            items: 'inventory-operation-report__issues',
            item: 'inventory-operation-report__issue',
            more: 'inventory-operation-report__more',
        },
        attributes: {role: 'region', 'aria-live': 'polite'},
        closeLabel: t('close', 'Close'),
        onClose: clearInventoryOperationReport,
    });
};

export const focusInventoryOperationReport = () => {
    const focusReport = () => {
        const report = $(REPORT_SELECTOR).find('.inventory-operation-report').first();
        if (!report.length) {
            return;
        }
        focusOperationReport(report[0]);
    };
    const openModal = document.querySelector('.modal.show');
    if (openModal) {
        openModal.addEventListener('hidden.bs.modal', () => window.requestAnimationFrame(focusReport), {once: true});
        return;
    }
    focusReport();
};

const STATE_PRESENTATION = {
    success: {
        backgroundClass: 'inventory-operation-overlay--success',
        label: () => t('completed', 'Completed'),
        messageClassName: 'inventory-operation-state inventory-operation-state--success',
    },
    failed: {
        backgroundClass: 'inventory-operation-overlay--failed',
        label: () => t('error', 'Error'),
        messageClassName: 'inventory-operation-state inventory-operation-state--failed',
    },
    warning: {
        backgroundClass: 'inventory-operation-overlay--warning',
        label: (item) => item.completed
            ? t('completedWithWarning', 'Completed with warning')
            : t('notCompleted', 'Not completed'),
        messageClassName: 'inventory-operation-state inventory-operation-state--warning',
    },
    scheduled: {
        backgroundClass: 'inventory-operation-overlay--scheduled',
        label: () => t('scheduled', 'Scheduled'),
        messageClassName: 'inventory-operation-state inventory-operation-state--scheduled',
    },
};

export const showInventoryItemStates = (items, {duration = 1200} = {}) => {
    const renderedOverlays = [];
    (Array.isArray(items) ? items : []).forEach((item) => {
        if (!item?.adId) {
            return;
        }
        const presentation = STATE_PRESENTATION[item.state] || STATE_PRESENTATION.failed;
        const overlayToken = (inventoryOverlayTokens.get(item.adId) || 0) + 1;
        inventoryOverlayTokens.set(item.adId, overlayToken);
        renderedOverlays.push({adId: item.adId, overlayToken});
        displayAdOverlay(
            item.adId,
            presentation.backgroundClass,
            presentation.label(item),
            {messageClassName: presentation.messageClassName},
        );
    });

    return new Promise((resolve) => {
        window.setTimeout(() => {
            renderedOverlays.forEach(({adId, overlayToken}) => {
                if (inventoryOverlayTokens.get(adId) === overlayToken) {
                    removeAdOverlay(adId);
                }
            });
            resolve();
        }, Math.max(0, Number(duration) || 0));
    });
};
